// src/constants/permissions.js
// Permission definitions for role-based access control

// ============================================================================
// PERMISSION CONSTANTS
// ============================================================================

const PERMISSIONS = {
  // ============================================================================
  // GRIEVANCE PERMISSIONS
  // ============================================================================
  VIEW_ALL_GRIEVANCES: "view_all_grievances",
  MANAGE_GRIEVANCES: "manage_grievances",
  VIEW_CONFIDENTIAL_GRIEVANCES: "view_confidential_grievances",
  VIEW_GRIEVANCE_WORKFLOWS: "view_grievance_workflows",
  MANAGE_GRIEVANCE_WORKFLOWS: "manage_grievance_workflows",
  FILE_GRIEVANCE_FOR_ANY_DEPARTMENT: "file_grievance_for_any_department",

  // ============================================================================
  // COMMUNICATION PERMISSIONS
  // ============================================================================
  SEND_BULK_EMAIL: "send_bulk_email",
  SEND_BULK_SMS: "send_bulk_sms",
  SEND_PUSH_NOTIFICATIONS: "send_push_notifications",
  CREATE_POSTS: "create_posts",
  MANAGE_POSTS: "manage_posts",
  PIN_POSTS: "pin_posts",

  // ============================================================================
  // DOCUMENT PERMISSIONS
  // ============================================================================
  UPLOAD_DOCUMENTS: "upload_documents",
  MANAGE_DOCUMENTS: "manage_documents",
  DELETE_ANY_DOCUMENT: "delete_any_document",

  // ============================================================================
  // EVENT PERMISSIONS
  // ============================================================================
  CREATE_EVENTS: "create_events",
  MANAGE_EVENTS: "manage_events",
  DELETE_ANY_EVENT: "delete_any_event",
  VIEW_LEADERSHIP_EVENTS: "view_leadership_events",
  VIEW_MEDIA_EVENTS: "view_media_events",

  // ============================================================================
  // HALL RESERVATION PERMISSIONS
  // ============================================================================
  APPROVE_HALL_RESERVATIONS: "approve_hall_reservations",
  MANAGE_HALL_RESERVATIONS: "manage_hall_reservations",

  // ============================================================================
  // USER & ADMIN PERMISSIONS
  // ============================================================================
  VIEW_ALL_USERS: "view_all_users",
  MANAGE_USERS: "manage_users",
  MANAGE_USER_NOTES: "manage_user_notes",
  MANAGE_ROLES: "manage_roles",
  MANAGE_DEPARTMENTS: "manage_departments",
  VIEW_AUDIT_LOGS: "view_audit_logs",
  VIEW_ANALYTICS: "view_analytics",

  // ============================================================================
  // LEADERSHIP PERMISSIONS
  // ============================================================================
  MANAGE_LEADERSHIP: "manage_leadership",

  // ============================================================================
  // FINANCIAL PERMISSIONS (V1.1+)
  // ============================================================================
  VIEW_FINANCIALS: "view_financials",
  MANAGE_FINANCIALS: "manage_financials",
  APPROVE_EXPENSES: "approve_expenses",

  // ============================================================================
  // VOTING PERMISSIONS (V1.1+)
  // ============================================================================
  CREATE_VOTES: "create_votes",
  MANAGE_VOTES: "manage_votes",
  MANAGE_POLLS: "manage_polls",
  SEE_POLL_RESPONDENTS: "see_poll_respondents",
  AUDIT_VOTES: "audit_votes",
  RELEASE_VOTE_RESULTS: "release_vote_results",
  OBSERVE_VOTE: "observe_vote",
  REVIEW_VOTER_ROLL: "review_voter_roll",
  MANAGE_ELIGIBILITY_CHALLENGES: "manage_eligibility_challenges",
  SUBMIT_TEST_BALLOT: "submit_test_ballot",

  // ============================================================================
  // STORE PERMISSIONS (V1.1+)
  // ============================================================================
  MANAGE_STORE: "manage_store",
  VIEW_STORE_ORDERS: "view_store_orders",
  MANAGE_STORE_PRODUCTS: "manage_store_products",
  MANAGE_STORE_ORDERS: "manage_store_orders",
  VIEW_STORE_COST_BASIS: "view_store_cost_basis",
  MANAGE_STORE_INVENTORY: "manage_store_inventory",

  // ============================================================================
  // ON-CALL PERMISSIONS
  // ============================================================================
  MANAGE_ON_CALL: "manage_on_call",
  TOGGLE_ON_CALL: "toggle_on_call",

  // ============================================================================
  // CONTRACT & BARGAINING PERMISSIONS
  // ============================================================================
  MANAGE_CONTRACTS: "manage_contracts",
  RATIFY_CONTRACT: "ratify_contract",
  VIEW_CONTRACT_HISTORY: "view_contract_history",
  VIEW_FEDERATION_CONTRACTS: "view_federation_contracts",
  VIEW_BARGAINING_NOTES: "view_bargaining_notes",
  MANAGE_BARGAINING_NOTES: "manage_bargaining_notes",

  // ============================================================================
  // TASK MANAGEMENT PERMISSIONS
  // ============================================================================
  VIEW_ALL_TASKS: "view_all_tasks",
  MANAGE_TASKS: "manage_tasks",
  ASSIGN_TASKS: "assign_tasks",
  VIEW_TASK_ANALYTICS: "view_task_analytics",

  // ============================================================================
  // EBOARD UPDATES PERMISSIONS
  // ============================================================================
  VIEW_EBOARD_UPDATES: "view_eboard_updates",
  SUBMIT_EBOARD_UPDATES: "submit_eboard_updates",
  MANAGE_EBOARD_UPDATES: "manage_eboard_updates",

  // ============================================================================
  // FORUM PERMISSIONS
  // ============================================================================
  MANAGE_FORUM: "manage_forum", // Delete any post/reply, lock threads, pin threads, manage categories
  PIN_FORUM_THREADS: "pin_forum_threads", // Pin threads without full manage access

  // ============================================================================
  // SUGGESTION BOX PERMISSIONS
  // ============================================================================
  MANAGE_SUGGESTION_BOX: "manage_suggestion_box",
  VIEW_SUGGESTION_BOX_RESPONSES: "view_suggestion_box_responses",

  // ============================================================================
  // PEER SUPPORT PERMISSIONS
  // ============================================================================
  MANAGE_PEER_SUPPORT: "manage_peer_support",
  PROVIDE_PEER_SUPPORT: "provide_peer_support",

  // ============================================================================
  // POLITICAL INTELLIGENCE PERMISSIONS
  // ============================================================================
  VIEW_OFFICIALS: "view_officials", // Full CRM dossier access (leadership)
  MANAGE_OFFICIALS: "manage_officials", // Create/edit officials, log interactions, manage staffers

  // ============================================================================
  // REP FINDER PERMISSIONS
  // ============================================================================
  MANAGE_REP_FINDER: "manage_rep_finder", // Create/edit/publish the Find Your Rep flowchart

  // ============================================================================
  // SYSTEM PERMISSIONS
  // ============================================================================
  MANAGE_TENANTS: "manage_tenants", // Super admin only
  MANAGE_SYSTEM_SETTINGS: "manage_system_settings",
  VIEW_DISABLED_FEATURES: "view_disabled_features",

  // ============================================================================
  // DOCUMENT VISIBILITY PERMISSIONS (local-controlled)
  // ============================================================================
  VIEW_LEADERSHIP_DOCUMENTS: "view_leadership_documents",
  MANAGE_DOCUMENT_VISIBILITY: "manage_document_visibility",

  // ============================================================================
  // FEDERATION — PARENT TIER PERMISSIONS
  // Held by state/district/international tenants. Controls cross-tenant actions.
  // ============================================================================
  MANAGE_FEDERATION_MEMBERSHIPS: "manage_federation_memberships",
  MANAGE_SHARED_OFFICIALS: "manage_shared_officials",
  POST_TO_FEDERATION_INTERNAL: "post_to_federation_internal",
  POST_TO_FEDERATION_EXTERNAL: "post_to_federation_external",
  PUSH_DOCUMENTS_TO_MEMBERS: "push_documents_to_members",
  VIEW_LOCAL_MEMBER_DIRECTORY: "view_local_member_directory",
  VIEW_ALL_LOCAL_OFFICIAL_NOTES: "view_all_local_official_notes",
  POST_FEDERATION_OFFICIAL_INTERACTION: "post_federation_official_interaction",
  SHARE_OFFICIAL_DOCUMENTS: "share_official_documents",
  MANAGE_FEDERATION_SETTINGS: "manage_federation_settings",
  GRANT_DELEGATION_PERMISSIONS: "grant_delegation_permissions",

  // ============================================================================
  // FEDERATION — LOCAL PERMISSIONS
  // Available to locals once their parent tier enables them via policy.
  // ============================================================================
  VIEW_FEDERATION_POSTS: "view_federation_posts",
  REPOST_FEDERATION_CONTENT: "repost_federation_content",
  VIEW_STATEWIDE_DOCUMENTS: "view_statewide_documents",
  VIEW_STATEWIDE_LEADERS: "view_statewide_leaders",
  VIEW_INTERSTATE_LEADERS: "view_interstate_leaders",
  VIEW_STATE_OFFICIAL_NOTES: "view_state_official_notes",
  VIEW_FEDERATION_OFFICIAL_INTERACTIONS:
    "view_federation_official_interactions",
  SHARE_DOCUMENTS_TO_FEDERATION: "share_documents_to_federation",
  SHARE_LEADERSHIP_TO_FEDERATION: "share_leadership_to_federation",
  SHARE_CONTRACTS_TO_FEDERATION: "share_contracts_to_federation",

  // ============================================================================
  // FEDERATION — PLATFORM SUPER-ADMIN ONLY
  // ============================================================================
  SYNC_EXTERNAL_OFFICIALS: "sync_external_officials",
  MANAGE_FEDERATION_ROOTS: "manage_federation_roots",
  CONFIGURE_MEMBER_DATA_SHARING: "configure_member_data_sharing",
  VIEW_ALL_FEDERATION_AUDIT_LOGS: "view_all_federation_audit_logs",
};

// ============================================================================
// PERMISSION METADATA
// ============================================================================

/**
 * Human-readable permission metadata
 * Used for admin UI and documentation
 */
const PERMISSION_METADATA = {
  // Grievances
  [PERMISSIONS.VIEW_ALL_GRIEVANCES]: {
    name: "View All Grievances",
    description: "View all member grievances (not just own grievances)",
    category: "Grievances",
  },
  [PERMISSIONS.MANAGE_GRIEVANCES]: {
    name: "Manage Grievances",
    description: "Edit grievance status, assign grievances, add internal notes",
    category: "Grievances",
  },
  [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: {
    name: "View Confidential Grievances",
    description: "View grievances marked as confidential",
    category: "Grievances",
  },
  [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: {
    name: "View Grievance Workflows",
    description: "View grievance workflow configurations and steps",
    category: "Grievances",
  },
  [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: {
    name: "Manage Grievance Workflows",
    description: "Create and edit grievance workflow configurations",
    category: "Grievances",
  },
  [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: {
    name: "File Grievance For Any Department",
    description: "File a grievance on behalf of members from any department",
    category: "Grievances",
  },

  // Communications
  [PERMISSIONS.SEND_BULK_EMAIL]: {
    name: "Send Bulk Email",
    description: "Send email to all members or groups",
    category: "Communications",
  },
  [PERMISSIONS.SEND_BULK_SMS]: {
    name: "Send Bulk SMS",
    description: "Send text messages to all members or groups",
    category: "Communications",
  },
  [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: {
    name: "Send Push Notifications",
    description: "Send push notifications to members' devices",
    category: "Communications",
  },
  [PERMISSIONS.CREATE_POSTS]: {
    name: "Create Posts",
    description: "Create news feed posts",
    category: "Communications",
  },
  [PERMISSIONS.MANAGE_POSTS]: {
    name: "Manage Posts",
    description: "Edit and delete any member's posts",
    category: "Communications",
  },
  [PERMISSIONS.PIN_POSTS]: {
    name: "Pin Posts",
    description: "Pin important posts to the top of the news feed",
    category: "Communications",
  },

  // Documents
  [PERMISSIONS.UPLOAD_DOCUMENTS]: {
    name: "Upload Documents",
    description: "Upload documents to the document library",
    category: "Documents",
  },
  [PERMISSIONS.MANAGE_DOCUMENTS]: {
    name: "Manage Documents",
    description: "Edit, delete, and organize all documents",
    category: "Documents",
  },
  [PERMISSIONS.DELETE_ANY_DOCUMENT]: {
    name: "Delete Any Document",
    description: "Delete any document regardless of who uploaded it",
    category: "Documents",
  },

  // Events
  [PERMISSIONS.CREATE_EVENTS]: {
    name: "Create Events",
    description: "Create new events and add them to the calendar",
    category: "Events",
  },
  [PERMISSIONS.MANAGE_EVENTS]: {
    name: "Manage Events",
    description: "Edit and delete any events",
    category: "Events",
  },
  [PERMISSIONS.DELETE_ANY_EVENT]: {
    name: "Delete Any Event",
    description: "Delete any event regardless of who created it",
    category: "Events",
  },
  [PERMISSIONS.VIEW_LEADERSHIP_EVENTS]: {
    name: "View Leadership Events",
    description:
      "View leadership-only calendar events (negotiations, political strategy, important dates)",
    category: "Events",
  },
  [PERMISSIONS.VIEW_MEDIA_EVENTS]: {
    name: "View Media Campaign Events",
    description: "View media and social campaign calendar events",
    category: "Events",
  },

  // Hall Reservations
  [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: {
    name: "Approve Hall Reservations",
    description: "Approve or reject union hall reservation requests",
    category: "Hall Reservations",
  },
  [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: {
    name: "Manage Hall Reservations",
    description: "Full control over hall reservation system",
    category: "Hall Reservations",
  },

  // Admin
  [PERMISSIONS.VIEW_ALL_USERS]: {
    name: "View All Users",
    description: "View full member directory including private info",
    category: "Administration",
  },
  [PERMISSIONS.MANAGE_USERS]: {
    name: "Manage Users",
    description: "Create, edit, activate, and deactivate user accounts",
    category: "Administration",
  },
  [PERMISSIONS.MANAGE_USER_NOTES]: {
    name: "Manage User Notes",
    description:
      "View and manage confidential notes about members (for member welfare tracking)",
    category: "Administration",
  },
  [PERMISSIONS.MANAGE_ROLES]: {
    name: "Manage Roles",
    description: "Create custom roles and assign roles to users",
    category: "Administration",
  },
  [PERMISSIONS.MANAGE_DEPARTMENTS]: {
    name: "Manage Departments",
    description: "Create, edit, and delete fire departments",
    category: "Administration",
  },
  [PERMISSIONS.VIEW_AUDIT_LOGS]: {
    name: "View Audit Logs",
    description: "View system audit logs and security events",
    category: "Administration",
  },
  [PERMISSIONS.VIEW_ANALYTICS]: {
    name: "View Analytics",
    description: "Access analytics dashboards and reports",
    category: "Administration",
  },

  // Leadership
  [PERMISSIONS.MANAGE_LEADERSHIP]: {
    name: "Manage Leadership",
    description: "Update leadership positions and terms",
    category: "Leadership",
  },

  // Financial (V1.1+)
  [PERMISSIONS.VIEW_FINANCIALS]: {
    name: "View Financials",
    description: "View financial data and reports",
    category: "Financial",
  },
  [PERMISSIONS.MANAGE_FINANCIALS]: {
    name: "Manage Financials",
    description: "Edit financial records and manage budgets",
    category: "Financial",
  },
  [PERMISSIONS.APPROVE_EXPENSES]: {
    name: "Approve Expenses",
    description: "Approve expense reimbursement requests",
    category: "Financial",
  },

  // Voting (V1.1+)
  [PERMISSIONS.CREATE_VOTES]: {
    name: "Create Votes",
    description: "Create new votes and polls for members",
    category: "Voting",
  },
  [PERMISSIONS.MANAGE_VOTES]: {
    name: "Manage Votes",
    description: "Manage active votes and view results",
    category: "Voting",
  },
  [PERMISSIONS.MANAGE_POLLS]: {
    name: "Manage Polls",
    description: "Create, edit, close, and delete polls for members",
    category: "Voting",
  },
  [PERMISSIONS.SEE_POLL_RESPONDENTS]: {
    name: "See Poll Respondents",
    description: "View which members responded to a poll and how they answered",
    category: "Voting",
  },
  [PERMISSIONS.AUDIT_VOTES]: {
    name: "Audit Votes",
    description:
      "View anonymous ballot totals in real-time and perform manual counts",
    category: "Voting",
  },
  [PERMISSIONS.RELEASE_VOTE_RESULTS]: {
    name: "Release Vote Results",
    description: "Publish vote results to all members after a vote closes",
    category: "Voting",
  },
  [PERMISSIONS.OBSERVE_VOTE]: {
    name: "Observe Vote",
    description:
      "Designated election observer: view participation statistics and post-close audit ballots for votes where assigned",
    category: "Voting",
  },
  [PERMISSIONS.REVIEW_VOTER_ROLL]: {
    name: "Review Voter Roll",
    description:
      "View the eligible voter list during the PENDING_REVIEW phase of an officer election",
    category: "Voting",
  },
  [PERMISSIONS.MANAGE_ELIGIBILITY_CHALLENGES]: {
    name: "Manage Eligibility Challenges",
    description:
      "Resolve voter eligibility challenges filed during the voter roll review period",
    category: "Voting",
  },
  [PERMISSIONS.SUBMIT_TEST_BALLOT]: {
    name: "Submit Test Ballot",
    description:
      "Submit test ballots during the DRAFT phase for Logic & Accuracy verification",
    category: "Voting",
  },

  // Store (V1.1+)
  [PERMISSIONS.MANAGE_STORE]: {
    name: "Manage Store",
    description: "Manage store inventory and products",
    category: "Store",
  },
  [PERMISSIONS.VIEW_STORE_ORDERS]: {
    name: "View Store Orders",
    description: "View all store orders and order history",
    category: "Store",
  },
  [PERMISSIONS.MANAGE_STORE_PRODUCTS]: {
    name: "Manage Store Products",
    description: "Add, edit, and delete store products and categories",
    category: "Store",
  },
  [PERMISSIONS.MANAGE_STORE_ORDERS]: {
    name: "Manage Store Orders",
    description: "View all orders, update order status, and verify payments",
    category: "Store",
  },
  [PERMISSIONS.VIEW_STORE_COST_BASIS]: {
    name: "View Store Cost Basis",
    description: "See product cost and vendor/purchase source information",
    category: "Store",
  },
  [PERMISSIONS.MANAGE_STORE_INVENTORY]: {
    name: "Manage Store Inventory",
    description: "Post manual inventory adjustments and reconciliations",
    category: "Store",
  },

  // On-Call
  [PERMISSIONS.MANAGE_ON_CALL]: {
    name: "Manage On-Call Settings",
    description: "Configure phone numbers and on-call queue settings",
    category: "On-Call",
  },
  [PERMISSIONS.TOGGLE_ON_CALL]: {
    name: "Toggle On-Call Status",
    description:
      "Go on-call or off-call to receive forwarded shift rep calls, and view voicemails",
    category: "On-Call",
  },

  // Contracts & Bargaining
  [PERMISSIONS.MANAGE_CONTRACTS]: {
    name: "Manage Contracts",
    description: "Create, edit, and manage collective bargaining agreements and MOUs",
    category: "Bargaining",
  },
  [PERMISSIONS.RATIFY_CONTRACT]: {
    name: "Ratify Contract",
    description: "Mark a negotiated contract as ratified and set it as the active agreement",
    category: "Bargaining",
  },
  [PERMISSIONS.VIEW_CONTRACT_HISTORY]: {
    name: "View Contract History",
    description: "View previous contract generations and article-level redline history",
    category: "Bargaining",
  },
  [PERMISSIONS.VIEW_FEDERATION_CONTRACTS]: {
    name: "View Federation Contracts",
    description: "View ratified contracts shared by sister locals in the federation",
    category: "Bargaining",
  },
  [PERMISSIONS.VIEW_BARGAINING_NOTES]: {
    name: "View Bargaining Notes",
    description: "View contract bargaining notes and negotiations",
    category: "Bargaining",
  },
  [PERMISSIONS.MANAGE_BARGAINING_NOTES]: {
    name: "Manage Bargaining Notes",
    description: "Create and edit bargaining notes",
    category: "Bargaining",
  },

  // Task Management
  [PERMISSIONS.VIEW_ALL_TASKS]: {
    name: "View All Tasks",
    description: "View all tasks in the organization (not just assigned tasks)",
    category: "Task Management",
  },
  [PERMISSIONS.MANAGE_TASKS]: {
    name: "Manage Tasks",
    description: "Create, edit, and delete tasks",
    category: "Task Management",
  },
  [PERMISSIONS.ASSIGN_TASKS]: {
    name: "Assign Tasks",
    description: "Assign and reassign tasks to other members",
    category: "Task Management",
  },
  [PERMISSIONS.VIEW_TASK_ANALYTICS]: {
    name: "View Task Analytics",
    description: "Access task analytics and completion metrics",
    category: "Task Management",
  },

  // E-Board Updates
  [PERMISSIONS.VIEW_EBOARD_UPDATES]: {
    name: "View E-Board Updates",
    description: "View all E-Board member monthly updates",
    category: "E-Board",
  },
  [PERMISSIONS.SUBMIT_EBOARD_UPDATES]: {
    name: "Submit E-Board Updates",
    description: "Submit monthly E-Board updates for meetings",
    category: "E-Board",
  },
  [PERMISSIONS.MANAGE_EBOARD_UPDATES]: {
    name: "Manage E-Board Updates",
    description: "Edit or delete any E-Board member's updates",
    category: "E-Board",
  },

  // Forum
  [PERMISSIONS.MANAGE_FORUM]: {
    name: "Manage Forum",
    description:
      "Delete any forum post or reply, lock threads, pin threads, and manage forum categories",
    category: "Forum",
  },
  [PERMISSIONS.PIN_FORUM_THREADS]: {
    name: "Pin Forum Threads",
    description: "Pin important forum threads to the top of the list",
    category: "Forum",
  },

  // Suggestion Box
  [PERMISSIONS.MANAGE_SUGGESTION_BOX]: {
    name: "Manage Suggestion Box",
    description:
      "Create and close topics, view all suggestions with author identity, post official replies, delete suggestions",
    category: "Suggestion Box",
  },
  [PERMISSIONS.VIEW_SUGGESTION_BOX_RESPONSES]: {
    name: "View Suggestion Box Responses",
    description:
      "Read-only access to all suggestions and author identities (no topic or reply management)",
    category: "Suggestion Box",
  },

  // Peer Support
  [PERMISSIONS.MANAGE_PEER_SUPPORT]: {
    name: "Manage Peer Support",
    description:
      "Full admin access — view all requests, manage worker roster, configure categories/dispositions/settings, and receive escalation alerts",
    category: "Peer Support",
  },
  [PERMISSIONS.PROVIDE_PEER_SUPPORT]: {
    name: "Provide Peer Support",
    description:
      "Appear in the worker roster, toggle on-call status, accept and close member requests",
    category: "Peer Support",
  },

  // Political Intelligence
  [PERMISSIONS.VIEW_OFFICIALS]: {
    name: "View Officials (Full Dossier)",
    description:
      "View full political intelligence dossiers including personal notes, stances, and interaction history",
    category: "Political Intelligence",
  },
  [PERMISSIONS.MANAGE_OFFICIALS]: {
    name: "Manage Officials",
    description:
      "Create and edit officials, log interactions, update stances, and manage staffers",
    category: "Political Intelligence",
  },

  // Rep Finder
  [PERMISSIONS.MANAGE_REP_FINDER]: {
    name: "Manage Rep Finder",
    description: "Create, edit, and publish the Find Your Rep flowchart",
    category: "Rep Finder",
  },

  // System
  [PERMISSIONS.MANAGE_TENANTS]: {
    name: "Manage Tenants",
    description: "Super admin permission to manage all tenants",
    category: "System",
  },
  [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: {
    name: "Manage System Settings",
    description: "Configure system-wide settings",
    category: "System",
  },
  [PERMISSIONS.VIEW_DISABLED_FEATURES]: {
    name: "View Disabled Features",
    description: "Access features that are disabled for general membership",
    category: "System",
  },

  // Document Visibility
  [PERMISSIONS.VIEW_LEADERSHIP_DOCUMENTS]: {
    name: "View Leadership Documents",
    description: "See documents restricted to leadership only",
    category: "Documents",
  },
  [PERMISSIONS.MANAGE_DOCUMENT_VISIBILITY]: {
    name: "Manage Document Visibility",
    description:
      "Promote leadership-only documents to all members or restrict them",
    category: "Documents",
  },

  // Federation — Parent Tier
  [PERMISSIONS.MANAGE_FEDERATION_MEMBERSHIPS]: {
    name: "Manage Federation Memberships",
    description: "Add or remove member tenants from this federation",
    category: "Federation",
  },
  [PERMISSIONS.MANAGE_SHARED_OFFICIALS]: {
    name: "Manage Shared Officials",
    description:
      "Create and edit canonical political official records in the shared pool",
    category: "Federation",
  },
  [PERMISSIONS.POST_TO_FEDERATION_INTERNAL]: {
    name: "Post to Federation (Internal)",
    description: "Create posts visible to leadership in all member locals",
    category: "Federation",
  },
  [PERMISSIONS.POST_TO_FEDERATION_EXTERNAL]: {
    name: "Post to Federation (External)",
    description:
      "Create posts that member locals can repost to their general feed",
    category: "Federation",
  },
  [PERMISSIONS.PUSH_DOCUMENTS_TO_MEMBERS]: {
    name: "Push Documents to Members",
    description:
      "Distribute a document to all member locals' document libraries",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_LOCAL_MEMBER_DIRECTORY]: {
    name: "View Local Member Directory",
    description:
      "Access field-filtered membership records from all member locals",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_ALL_LOCAL_OFFICIAL_NOTES]: {
    name: "View All Local Official Notes",
    description:
      "See federation-visible notes on political officials from all member locals",
    category: "Federation",
  },
  [PERMISSIONS.POST_FEDERATION_OFFICIAL_INTERACTION]: {
    name: "Post Federation-Wide Interaction",
    description:
      "Log a political interaction visible to all locals in the federation",
    category: "Federation",
  },
  [PERMISSIONS.SHARE_OFFICIAL_DOCUMENTS]: {
    name: "Share Official Documents",
    description:
      "Make a political document visible to all locals in the federation",
    category: "Federation",
  },
  [PERMISSIONS.MANAGE_FEDERATION_SETTINGS]: {
    name: "Manage Federation Settings",
    description:
      "Configure federation policies and permission policies for member locals",
    category: "Federation",
  },
  [PERMISSIONS.GRANT_DELEGATION_PERMISSIONS]: {
    name: "Grant Delegation Permissions",
    description:
      "Create delegated access for users from member locals to act within this tenant",
    category: "Federation",
  },

  // Federation — Local
  [PERMISSIONS.VIEW_FEDERATION_POSTS]: {
    name: "View Federation Posts",
    description:
      "See posts from parent tier (state/district/international) in the feed",
    category: "Federation",
  },
  [PERMISSIONS.REPOST_FEDERATION_CONTENT]: {
    name: "Repost Federation Content",
    description:
      "Share a federation post to the local feed for all members to see",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_STATEWIDE_DOCUMENTS]: {
    name: "View Statewide Documents",
    description:
      "Access shared contracts and documents from peer locals and the state",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_STATEWIDE_LEADERS]: {
    name: "View Statewide Leadership",
    description: "See shared leadership positions from all locals in the state",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_INTERSTATE_LEADERS]: {
    name: "View Interstate Leadership",
    description:
      "See shared leadership positions from locals across all states",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_STATE_OFFICIAL_NOTES]: {
    name: "View State Official Notes",
    description:
      "See notes on political officials marked as visible to all locals",
    category: "Federation",
  },
  [PERMISSIONS.VIEW_FEDERATION_OFFICIAL_INTERACTIONS]: {
    name: "View Federation Official Interactions",
    description:
      "See political interactions logged as federation-visible by other locals",
    category: "Federation",
  },
  [PERMISSIONS.SHARE_DOCUMENTS_TO_FEDERATION]: {
    name: "Share Documents to Federation",
    description:
      "Mark a local document as visible to peer locals or the parent tier",
    category: "Federation",
  },
  [PERMISSIONS.SHARE_LEADERSHIP_TO_FEDERATION]: {
    name: "Share Leadership to Federation",
    description:
      "Mark leadership positions as visible to peer locals in the federation",
    category: "Federation",
  },
  [PERMISSIONS.SHARE_CONTRACTS_TO_FEDERATION]: {
    name: "Share Contracts to Federation",
    description:
      "Mark a contract as shared upward to the state and peer locals",
    category: "Federation",
  },

  // Federation — Platform Super-Admin
  [PERMISSIONS.SYNC_EXTERNAL_OFFICIALS]: {
    name: "Sync External Officials",
    description:
      "Trigger a sync of political officials from external APIs (Open States, ProPublica)",
    category: "System",
  },
  [PERMISSIONS.MANAGE_FEDERATION_ROOTS]: {
    name: "Manage Federation Roots",
    description:
      "Create and configure federation tree roots (platform super-admin only)",
    category: "System",
  },
  [PERMISSIONS.CONFIGURE_MEMBER_DATA_SHARING]: {
    name: "Configure Member Data Sharing",
    description:
      "Set which member fields are accessible to the parent federation (super-admin only)",
    category: "System",
  },
  [PERMISSIONS.VIEW_ALL_FEDERATION_AUDIT_LOGS]: {
    name: "View All Federation Audit Logs",
    description:
      "Access the cross-tenant access audit log (platform super-admin only)",
    category: "System",
  },
};

// ============================================================================
// PERMISSION CATEGORIES
// ============================================================================

const PERMISSION_CATEGORIES = [
  "Grievances",
  "Communications",
  "Documents",
  "Events",
  "Hall Reservations",
  "Administration",
  "Leadership",
  "Financial",
  "Voting",
  "Store",
  "On-Call",
  "Peer Support",
  "Bargaining",
  "Task Management",
  "E-Board",
  "Forum",
  "Suggestion Box",
  "Political Intelligence",
  "Rep Finder",
  "Federation",
  "System",
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all permissions grouped by category
 * @returns {Object} Permissions grouped by category
 */
function getPermissionsByCategory() {
  const grouped = {};

  PERMISSION_CATEGORIES.forEach((category) => {
    grouped[category] = [];
  });

  Object.entries(PERMISSION_METADATA).forEach(([key, metadata]) => {
    grouped[metadata.category].push({
      key,
      ...metadata,
    });
  });

  return grouped;
}

/**
 * Get all permissions as a flat array with metadata
 * @returns {Array} Array of permission objects
 */
function getAllPermissions() {
  return Object.entries(PERMISSION_METADATA).map(([key, metadata]) => ({
    key,
    ...metadata,
  }));
}

/**
 * Validate that a permission exists
 * @param {string} permission - Permission key to validate
 * @returns {boolean} True if permission exists
 */
function isValidPermission(permission) {
  return Object.values(PERMISSIONS).includes(permission);
}

/**
 * Get permission metadata
 * @param {string} permission - Permission key
 * @returns {Object|null} Permission metadata or null if not found
 */
function getPermissionMetadata(permission) {
  return PERMISSION_METADATA[permission] || null;
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  PERMISSIONS,
  PERMISSION_METADATA,
  PERMISSION_CATEGORIES,
  getPermissionsByCategory,
  getAllPermissions,
  isValidPermission,
  getPermissionMetadata,
};
